import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "493",
  "slug": "what-is-a-wetland-marshes-swamps-and-bogs",
  "title": "What Is a Wetland? Marshes, Swamps, and Bogs Explained",
  "excerpt": "Wetlands are neither fully land nor fully water, and they are some of the most valuable ecosystems on the planet. Here is how marshes, swamps, and bogs differ.",
  "date": "2026-06-22",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1633295686973-6ce4b697ebab?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx3ZXRsYW5kJTIwbWFyc2h8ZW58MXwwfHx8MTc4MjExNjAzOHww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "A wetland is land that is saturated with water for all or part of the year, sitting in the zone between dry land and open water. The main types are marshes (grassy), swamps (wooded), and bogs and fens (peat-forming) — and together they filter water, buffer floods, and store huge amounts of carbon.",
  "content": [
    {
      "type": "paragraph",
      "text": "Some landscapes refuse to pick a side. Wetlands are the great in-between of geography, neither solid ground you can build on nor open water you can sail across. For a long time they were dismissed as wasteland to be drained, but we now understand them as some of the hardest-working ecosystems on Earth. From space they often read as mottled patches of green and silver where land and water blur together."
    },
    {
      "type": "heading",
      "text": "What Counts as a Wetland?"
    },
    {
      "type": "paragraph",
      "text": "A wetland is an area where water covers the soil, or sits near the surface, for long enough each year to shape the soil and the plants that grow there. The defining feature is not a particular depth of water but saturation: the ground stays wet long enough that only specially adapted plants can survive, and the soil takes on the chemistry of being waterlogged."
    },
    {
      "type": "paragraph",
      "text": "That single idea covers an enormous range of places, from coastal salt marshes and mangrove forests to inland peat bogs and seasonal floodplains. What they share is that water is the dominant force shaping the landscape, even when there is no obvious lake or river in sight."
    },
    {
      "type": "heading",
      "text": "Marshes: The Grassy Wetlands"
    },
    {
      "type": "paragraph",
      "text": "Marshes are wetlands dominated by soft, non-woody plants such as grasses, reeds, and sedges. They form where water collects in shallow, often slow-moving conditions — along the edges of lakes and rivers, in low-lying ground, and where rivers meet the sea. Salt marshes line sheltered coasts and are flooded by the tides, while freshwater marshes fill inland basins. With few trees to block the view, marshes tend to look like open seas of waving grass."
    },
    {
      "type": "heading",
      "text": "Swamps: The Forested Wetlands"
    },
    {
      "type": "paragraph",
      "text": "Swamps are wetlands where trees and woody shrubs dominate instead of grasses. The water table sits at or above the surface for much of the year, and the trees that thrive there are specialists, like cypress and mangroves, able to root in waterlogged ground. Mangrove swamps along tropical coastlines are especially important: their tangled roots trap sediment, shelter young fish, and act as a natural barrier against storm surges."
    },
    {
      "type": "heading",
      "text": "Bogs and Fens: The Peat Builders"
    },
    {
      "type": "paragraph",
      "text": "Bogs and fens are wetlands where dead plant material piles up faster than it can rot, forming thick layers of peat. The difference between them comes down to where their water comes from. Bogs are fed mainly by rainfall, which makes them acidic and nutrient-poor, and they are often carpeted with spongy sphagnum moss. Fens are fed by groundwater, which makes them less acidic and able to support a richer mix of plants."
    },
    {
      "type": "paragraph",
      "text": "Peatlands matter far beyond their size. Because they lock away carbon in waterlogged, slowly decaying plant matter, they store more carbon per unit area than almost any other ecosystem on land, including forests. Drain them, and that ancient carbon begins to escape into the atmosphere."
    },
    {
      "type": "heading",
      "text": "Why Wetlands Matter"
    },
    {
      "type": "paragraph",
      "text": "Wetlands punch far above their weight. The same waterlogged conditions that make them awkward for farming or building give them a set of abilities that dry land simply cannot match:"
    },
    {
      "type": "list",
      "items": [
        "Water filtering: wetland plants and soils trap sediment and pollutants, cleaning water as it passes through.",
        "Flood control: they soak up heavy rain and river surges like a sponge, releasing it slowly and protecting downstream towns.",
        "Carbon storage: peatlands and coastal wetlands lock away vast amounts of carbon that would otherwise warm the climate.",
        "Biodiversity: they are nurseries and feeding grounds for fish, amphibians, and migratory birds, hosting a disproportionate share of wildlife.",
        "Coastal defence: mangroves and salt marshes blunt the force of storms and slow coastal erosion."
      ]
    },
    {
      "type": "paragraph",
      "text": "Some of the planet’s most remarkable wetlands have become household names: the Pantanal of South America, the largest tropical wetland on Earth; the Sundarbans mangrove forest straddling India and Bangladesh; Florida’s Everglades; and the Okavango Delta, where an entire river fans out and vanishes into the Kalahari sands. An international treaty, the Ramsar Convention, exists specifically to protect wetlands of global importance."
    },
    {
      "type": "heading",
      "text": "Spotting Wetlands From Above"
    },
    {
      "type": "paragraph",
      "text": "From a satellite view, wetlands give themselves away with a few clues: a patchwork of water and vegetation, branching channels that wander rather than run straight, and a fuzzy, indistinct boundary between land and water rather than a clean shoreline. Coastal wetlands often show the feathery texture of mangroves or the fine grain of tidal marsh."
    }
  ]
};
